import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BlocoBasico, { type BlocoBasicoHandle } from '@/pages/assistente-evento/components/BlocoBasico';
import ChecklistPublicar, { type ItemChecklist } from '../components/ChecklistPublicar';
import BlocoSoDivulgacao from '../components/BlocoSoDivulgacao';
import { useDivulgar } from './useDivulgar';

export default function CriarDivulgarPage() {
  const navigate = useNavigate();
  const refBasico = useRef<BlocoBasicoHandle>(null);

  const {
    estado,
    erro,
    eventoId,
    draft,
    mudarDraft,
    perfilCidade,
    perfilUf,
    estadoSalvamento,
    salvando,
    publicar,
    setErro,
    pendencias,
    melhorias,
  } = useDivulgar();

  const faltam = pendencias.filter((p) => !p.pronta).length;

  function focar(chave: string) {
    refBasico.current?.focar(chave);
  }

  const itensPendentes: ItemChecklist[] = pendencias.map((p) => ({
    chave: p.chave,
    rotulo: p.rotulo,
    pronta: p.pronta,
    aoClicar: () => focar(p.chave),
  }));

  const itensMelhorias: ItemChecklist[] = melhorias.map((m) => ({
    chave: m.chave,
    rotulo: m.rotulo,
    icone: m.icone,
    ajuda: m.ajuda,
    pronta: m.pronta,
    aoClicar: () => focar(m.chave),
  }));

  async function aoPublicar() {
    if (faltam > 0) return;
    const ok = refBasico.current?.validar();
    if (!ok) return;
    await publicar();
  }

  if (estado === 'carregando') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="h-6 w-64 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-64 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (estado === 'erro') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Não foi possível carregar</h1>
            <p className="mt-2 text-sm text-secondary-500">{erro}</p>
            <button
              type="button"
              onClick={() => navigate('/meus-eventos')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Voltar aos meus eventos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <header className="fixed inset-x-0 top-0 z-20 border-b border-secondary-200 bg-white">
        <div className="mx-auto w-full max-w-[720px] px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-sm font-medium text-foreground-950">
              {draft.nome.trim() || 'Novo evento'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/meus-eventos')}
              className="cursor-pointer whitespace-nowrap text-sm text-secondary-500 transition-colors hover:text-foreground-800"
            >
              Sair
            </button>
          </div>
          <p className="mt-0.5 text-xs text-secondary-500">Divulgar um evento · gratuito</p>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[720px] px-4 pb-40 pt-24">
        <div className="rounded-lg border border-secondary-200 bg-background-50 px-5 py-4 text-sm text-secondary-600">
          <span className="font-medium text-foreground-800">Tudo certo!</span> Preencha o básico e
          toque em <span className="font-medium text-foreground-800">Publicar</span>. Em segundos o
          seu evento está no ar, com o link pronto para compartilhar.
        </div>

        {erro && (
          <div className="mt-4 rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
            {erro}
            <button
              type="button"
              onClick={() => setErro(null)}
              className="ml-2 cursor-pointer font-medium underline"
            >
              Fechar
            </button>
          </div>
        )}

        <div className="mt-8">
          <BlocoBasico
            ref={refBasico}
            draft={draft}
            eventoId={eventoId}
            onChange={mudarDraft}
            perfilCidade={perfilCidade}
            perfilUf={perfilUf}
            estadoSalvamento={estadoSalvamento}
          />
        </div>

        <div className="mt-8">
          <BlocoSoDivulgacao eventoId={eventoId} />
        </div>

        <div className="mt-8">
          <ChecklistPublicar pendentes={itensPendentes} melhorias={itensMelhorias} />
        </div>
      </main>

      <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-secondary-200 bg-white">
        <div className="mx-auto w-full max-w-[720px] px-4 py-4">
          <button
            type="button"
            onClick={aoPublicar}
            disabled={salvando || faltam > 0}
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-500"
          >
            {salvando && (
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-loader-4-line animate-spin" />
              </span>
            )}
            {salvando ? 'Publicando…' : 'Publicar'}
          </button>
          {faltam > 0 && !salvando && (
            <p className="mt-2 text-center text-sm text-foreground-700">
              Falta {faltam} {faltam === 1 ? 'coisa' : 'coisas'} para colocar no ar.
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}